import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  api,
  ApiError,
  type DashboardBox,
  type DashboardQueue,
  type DashboardSection,
  type DashboardSectionIndex,
} from './api';
import { DURUM_ADI, ORTAK, para, ROL_ADI, tamSayi, TANIMSIZ, tarihSaat } from './metin/ortak';
import { PANO_METIN, SUTUN_ADI } from './metin/pano';
import { baglantiTiklamasi, girisYolu, siparisYolu, useYol, YOLLAR } from './yonlendirme';

/**
 * CRM-A14: dört bölümün ana ekranı (`docs/tasarim/dashboard.md` §8).
 *
 * Açılışta **grafik yok, toplam yok, hoş geldiniz yok** (§2.2). İlk kutu, o bölümün en yaşlı
 * bekleyen işidir. Her kuyruğun başlığında tek bir canlı sayı duruyor: yaş medyanı, saat
 * cinsinden (§7).
 *
 * Ekran hiçbir sayı üretmiyor: bütün değerler uçtan geliyor, boş kuyruk boş görünüyor ve
 * hesaplanamayan yaş "ölçülmüyor" diye yazılıyor. Ekrandaki her cümle `metin/pano.ts` içinde.
 */

/**
 * Genel bakışta panoların sırası. İK sona eklendi (30.08.2026): ilk dördü bugünün işini
 * gösteriyor, İK ise kadroyu — bir siparişten daha az acil, ve sıra aciliyeti anlatıyor.
 */
const SECTION_ORDER = ['OPERATIONS', 'FINANCE', 'SALES', 'MARKETING', 'HR'] as const;

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : PANO_METIN.istekHatasi;
}

/** Ekranda bir tarih değil bir süre okunur: "61s" bekleyen işin yaşıdır. */
function hours(value: number | null): string {
  return value === null ? PANO_METIN.kuyruk.yasYok : `${tamSayi(value)}${PANO_METIN.kuyruk.saatEki}`;
}

/**
 * Bir hücre. Tutar alanları hiç gelmediyse hiç çizilmez, gizlenmez, **yok**: uç, izni olmayan
 * aktöre o alanı zaten göndermiyor (tasarım §4.2, `oversight(actor, id)` deseni).
 */
/**
 * Para sütunları.
 *
 * Uç, tutarları **alt birim** olarak gönderiyor ve sütun adı bunu söylüyor (`amountMinor`,
 * `costMinor`, `outstandingMinor`, `totalMinor`). Ham hâliyle çizildiğinde `40000` yazıyordu ve
 * yanındaki sütunda `EUR`: operatör kırk bin euro okuyor, oysa dört yüz euro. Bu, çevrilmemiş bir
 * metinden kötü; **yanlış** bir sayı.
 *
 * Ad kuralına bakılıyor, sayının büyüklüğüne değil: `Minor` ile biten sütun paradır, bitmeyen
 * sütun bir sayaç ya da bir yaş olabilir ve bölünmemelidir.
 */
function paraSutunuMu(sutun: string): boolean {
  return sutun.endsWith('Minor');
}

/**
 * Ham tanı sütunları: dış bir sistemin döndürdüğü, sabit bir sözlüğe girmeyen serbest metin.
 *
 * `publishError` bugün tek örneği: bir kuponun v3'e yayınlanma denemesi başarısız olduğunda
 * uçtaki hata mesajı olduğu gibi saklanıyor (`FinanceCoupon.publishError`) ve İngilizce olabilir.
 * Uydurma bir çeviri bu değeri yanlış gösterirdi; `code` olarak çizilmesi onu bir UI cümlesi gibi
 * değil, alıntılanan bir dış kayıt gibi okutuyor (denetim izni satırındaki eylem koduyla aynı desen).
 */
function hamTanikSutunuMu(sutun: string): boolean {
  return sutun === 'publishError';
}

/** `id` sütunu bir UUID: 390 pikselde tek başına ekranın yarısını yiyordu. Kod sütunu (varsa)
 *  zaten insanın okuduğu kimlik; burası kırpılıp tam değer `title`de duruyor, aranabilir kalıyor. */
function kimlikSutunuMu(sutun: string): boolean {
  return sutun === 'id';
}

function Cell({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="muted">{TANIMSIZ}</span>;
  if (typeof value === 'boolean') return <>{value ? PANO_METIN.hucre.evet : PANO_METIN.hucre.hayir}</>;
  if (Array.isArray(value)) {
    // Bugün tek dizi alanı mutabakatın üç kapısı. Kapalı olanlar yazılır, açık olanlar değil.
    const gates = value.filter(
      (entry): entry is { key: string; closed: boolean; label: string } =>
        typeof entry === 'object' && entry !== null && 'closed' in entry && 'label' in entry,
    );
    if (gates.length === value.length && gates.length > 0) {
      const closed = gates.filter((gate) => gate.closed);
      if (closed.length === 0) return <span className="muted">{PANO_METIN.hucre.kapiYok}</span>;
      return (
        <>
          {closed.map((gate) => (
            <span className="state" key={gate.key} title={gate.key}>
              {gate.label}
            </span>
          ))}
        </>
      );
    }
    return <>{tamSayi(value.length)}</>;
  }
  if (typeof value === 'object') return <code>{JSON.stringify(value)}</code>;
  /**
   * Kod değeri geldiyse ekranda karşılığı yazılıyor: uç `IN_FULFILMENT` gönderiyor, operatör
   * "Operasyonda" okuyor. Karşılığı olmayan değer olduğu gibi kalıyor, çünkü bir kimliği ya da
   * bir kodu çevirmeye kalkmak onu bozmak olurdu.
   */
  const yazi = String(value);
  /*
   * Rol kodları da çevriliyor (30.08.2026, İK panosu 390 pikselde ölçülürken bulundu).
   *
   * Kuyruk tabloları uçtan gelen ham değerleri yazıyor ve karşılığı yalnız `DURUM_ADI` içinde
   * aranıyordu. İK kuyruğu bir rol taşıyan ilk kuyruk oldu ve ekranda `CRM_SALES` göründü:
   * Türkçe bir ekranda çevrilmemiş bir kod değeri. İki tablonun anahtarları çakışmıyor
   * (`ROL_ADI` sekiz rol, `DURUM_ADI` yetmiş üç durum), o yüzden sıra güvenli.
   */
  return <>{ROL_ADI[yazi] ?? DURUM_ADI[yazi] ?? yazi}</>;
}

/** Sütunlar, gelen satırların anahtarlarından. Uç ne gönderdiyse o çizilir, fazlası değil. */
function columnsOf(items: Record<string, unknown>[]): string[] {
  const seen: string[] = [];
  for (const item of items) {
    for (const key of Object.keys(item)) if (!seen.includes(key)) seen.push(key);
  }
  return seen;
}

/** Bir sipariş kodu mu: `ORD-` öneki. Sadece bu şekle sahip satırlar sipariş detayına bağlanıyor. */
function siparisKoduMu(deger: unknown): deger is string {
  return typeof deger === 'string' && deger.startsWith('ORD-');
}

/**
 * Tabloda çizilmeyen alanlar: satırın **eylemini** süren teknik değerler.
 *
 * `version` iyimser kilidin sürümü, `verifiableByMe` de artık bir sütun değil düğmenin kendi
 * durumu. İkisi de operatörün okuyacağı bir şey değil; sütun olarak durdukları sürece tabloyu
 * uzatıyor ve "Doğrulayabilir miyim: hayır" gibi bir hücre, gerekçesini söylemeden yalnız
 * olumsuzluk bildiriyordu. Gerekçe artık düğmenin üstünde.
 */
const EYLEM_SUTUNLARI = new Set(['version', 'verifiableByMe']);

/**
 * Kuyruk satırının kendi eylemi.
 *
 * Bugün tek örneği var: doğrulanmamış ödeme kanıtı. Kayıt bir tablo, koşullu bir `if` yığını
 * değil — ikinci kuyruk eylem kazandığında buraya bir satır ekleniyor, `Queue` değişmiyor.
 * Gerekçesi `metin/pano.ts` `eylem` başlığında yazılı.
 */
function OdemeDogrulaDugmesi({ item, yenile }: { item: Record<string, unknown>; yenile: () => void }) {
  const [calisiyor, setCalisiyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [bildirim, setBildirim] = useState<string | null>(null);
  const metin = PANO_METIN.eylem;

  const id = typeof item.id === 'string' ? item.id : null;
  const version = typeof item.version === 'number' ? item.version : null;
  const tutar = typeof item.amountMinor === 'number' ? item.amountMinor : null;
  const paraBirimi = typeof item.currencyCode === 'string' ? item.currencyCode : null;

  // İki ayrı "yapamazsın" var ve gerekçeleri ayrı. Tek bir gri düğme ikisini de gizlerdi.
  const engel = item.verifiableByMe === false ? metin.dogrulanamaz : null;
  const eksikVeri = tutar === null || id === null || version === null;

  if (engel !== null || eksikVeri) {
    return (
      <button
        type="button"
        className="ikincil"
        disabled
        title={engel ?? metin.tutarOkunamadi}
        data-testid="queue-verify-payment-disabled"
      >
        {metin.dogrula}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className="birincil"
        data-testid="queue-verify-payment"
        disabled={calisiyor}
        onClick={(olay) => {
          // Satırın kendisi siparişe gidiyor; düğme satırın değil kendisinin işini yapıyor.
          olay.stopPropagation();
          setHata(null);
          setBildirim(null);
          setCalisiyor(true);
          void api
            .verifyPayment(id, { expectedVersion: version, confirmedAmountMinor: tutar })
            .then((sonuc) => {
              setBildirim(sonuc.idempotentReplay ? metin.zatenDogrulandi : metin.dogrulandi(para(tutar, paraBirimi)));
              yenile();
            })
            .catch((yakalanan: unknown) => setHata(describe(yakalanan)))
            .finally(() => setCalisiyor(false));
        }}
      >
        {calisiyor ? metin.dogrulaniyor : metin.dogrula}
      </button>
      {hata ? (
        <span className="error" role="alert" data-testid="queue-verify-payment-error">
          {hata}
        </span>
      ) : null}
      {bildirim ? (
        <span role="status" data-testid="queue-verify-payment-notice">
          {bildirim}
        </span>
      ) : null}
    </>
  );
}

const KUYRUK_EYLEMI: Readonly<
  Record<string, (item: Record<string, unknown>, yenile: () => void) => ReactNode>
> = {
  'payments-recorded': (item, yenile) => <OdemeDogrulaDugmesi item={item} yenile={yenile} />,
};

function Queue({ queue, yenile }: { queue: DashboardQueue; yenile: () => void }) {
  const { git } = useYol();
  const columns = columnsOf(queue.items).filter((column) => !EYLEM_SUTUNLARI.has(column));
  const eylem = KUYRUK_EYLEMI[queue.key];
  const metin = PANO_METIN.kuyruk;
  return (
    <article data-testid={`queue-${queue.key}`}>
      <div className="section-heading">
        <div>
          <h3 style={{ margin: '5px 0 0', paddingTop: 0, borderTop: 0 }}>{queue.label}</h3>
        </div>
        <span className="count-badge" data-testid={`queue-${queue.key}-median`}>
          {queue.ages.measured ? `${metin.medyanYas} ${hours(queue.ages.medianAgeHours)}` : metin.yasOlculmuyor}
        </span>
      </div>
      <div className="filters">
        <span className="state" data-testid={`queue-${queue.key}-count`}>
          {tamSayi(queue.ages.count)} {ORTAK.kayit}
        </span>
        {queue.ages.measured ? (
          <>
            <span className="state">{metin.enYasli} {hours(queue.ages.oldestAgeHours)}</span>
            <span className="state">{metin.yirmiDortAlti} {tamSayi(queue.ages.buckets.LT_24H ?? 0)}</span>
            <span className="state">{metin.yirmiDortKirkSekiz} {tamSayi(queue.ages.buckets.H24_48H ?? 0)}</span>
            <span className="state" data-testid={`queue-${queue.key}-late`}>
              {metin.kirkSekizUstu} {tamSayi(queue.ages.buckets.GT_48H ?? 0)}
            </span>
          </>
        ) : null}
      </div>
      {queue.items.length === 0 ? (
        <div className="empty-state">
          <strong>{metin.bosBaslik}</strong>
          <span>{metin.bosTarif}</span>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {/* Sütun adı uçtan gelen anahtarın karşılığı; karşılığı yoksa anahtarın kendisi. */}
                {columns.map((column) => (
                  <th key={column}>{SUTUN_ADI[column] ?? column}</th>
                ))}
                {eylem ? <th>{PANO_METIN.eylem.sutunAdi}</th> : null}
              </tr>
            </thead>
            <tbody>
              {queue.items.map((item, index) => {
                // Satırın kendisi bir sipariş mi (`id`/`code` doğrudan siparişin), yoksa yalnız
                // başka bir siparişe mi bakıyor (`orderId`/`orderCode`, ör. bir görev satırı)?
                // İkisi de "sipariş koduna bağlantı yok" kusurunu düzeltiyor, ikisi de aynı hedefe.
                const dogrudanSiparisId = typeof item.id === 'string' && siparisKoduMu(item.code) ? item.id : null;
                const dolayliSiparisId =
                  !dogrudanSiparisId && typeof item.orderId === 'string' && siparisKoduMu(item.orderCode) ? item.orderId : null;
                const satirSiparisId = dogrudanSiparisId ?? dolayliSiparisId;
                return (
                  <tr
                    key={String(item.id ?? index)}
                    data-testid={`queue-${queue.key}-row`}
                    className={satirSiparisId ? 'row-link' : undefined}
                    onClick={satirSiparisId ? () => git(siparisYolu(satirSiparisId)) : undefined}
                  >
                    {columns.map((column) => {
                      const buSutunSiparisBaglantisi =
                        (column === 'code' && dogrudanSiparisId) || (column === 'orderCode' && dolayliSiparisId);
                      return (
                        <td key={column} className={kimlikSutunuMu(column) ? 'col-id' : undefined} title={kimlikSutunuMu(column) ? String(item[column] ?? '') : undefined}>
                          {buSutunSiparisBaglantisi ? (
                            <a href={siparisYolu(satirSiparisId as string)} onClick={baglantiTiklamasi(git, siparisYolu(satirSiparisId as string))}>
                              {String(item[column])}
                            </a>
                          ) : paraSutunuMu(column) && typeof item[column] === 'number' ? (
                            para(item[column] as number, typeof item.currencyCode === 'string' ? item.currencyCode : null)
                          ) : hamTanikSutunuMu(column) && item[column] ? (
                            // Bu bir UI cümlesi değil, dış bir sistemin ham tanısı (ör. yayın
                            // denemesinin v3'ten döndürdüğü İngilizce hata metni). `code` olarak
                            // duruyor: uydurulmuş bir çeviri, ham değerden kötüdür.
                            <code>{String(item[column])}</code>
                          ) : (
                            <Cell value={item[column]} />
                          )}
                        </td>
                      );
                    })}
                    {eylem ? (
                      <td className="kuyruk-eylemi" onClick={(olay) => olay.stopPropagation()}>
                        {eylem(item, yenile)}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {queue.notes.map((note) => (
        <p className="muted" key={note} style={{ fontSize: 12 }}>
          {note}
        </p>
      ))}
    </article>
  );
}

function Box({ box, yenile }: { box: DashboardBox; yenile: () => void }) {
  return (
    <div data-testid={`box-${box.key}`}>
      <h3>
        {box.label}
      </h3>
      {box.notes.map((note) => (
        <p className="muted" key={note} style={{ fontSize: 12 }}>
          {note}
        </p>
      ))}
      {box.lists.map((queue) => (
        <Queue key={queue.key} queue={queue} yenile={yenile} />
      ))}
    </div>
  );
}

function SectionScreen({ sectionKey }: { sectionKey: string }) {
  const [section, setSection] = useState<DashboardSection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const metin = PANO_METIN.bolum;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSection(await api.dashboardSection(sectionKey));
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setLoading(false);
    }
  }, [sectionKey]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      id={`dashboard-${sectionKey.toLowerCase()}`}
      data-testid={`dashboard-${sectionKey.toLowerCase()}`}
      aria-labelledby={`dashboard-${sectionKey.toLowerCase()}-title`}
    >
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id={`dashboard-${sectionKey.toLowerCase()}-title`}>{section?.label ?? sectionKey}</h2>
          <p className="muted">{section?.owns ?? ''}</p>
        </div>
        {section ? (
          <span className="count-badge" data-testid={`dashboard-${sectionKey.toLowerCase()}-badge`}>
            {tamSayi(section.lateThresholdHours)}{metin.gecikmeEki} {tamSayi(section.badgeLate)}
          </span>
        ) : (
          <span className="loading-chip">{ORTAK.yukleniyor}</span>
        )}
      </div>

      {error ? (
        <p className="error" data-testid={`dashboard-${sectionKey.toLowerCase()}-error`}>
          {error}
        </p>
      ) : null}
      {loading && !section ? <p className="muted">{metin.kuyruklarOkunuyor}</p> : null}

      {section?.blocker ? (
        <div className="callout" data-testid={`dashboard-${sectionKey.toLowerCase()}-blocker`} role="status">
          <span>
            <strong>{section.blocker.label}</strong>
            <br />
            {section.blocker.detail}
          </span>
        </div>
      ) : null}

      {section ? (
        <>
          <h3>{metin.bant1}</h3>
          {section.band1.map((box) => (
            <Box key={box.key} box={box} yenile={() => void load()} />
          ))}

          {section.band2.length > 0 ? (
            <details data-testid={`dashboard-${sectionKey.toLowerCase()}-band2`}>
              <summary>
                {metin.bant2} ({tamSayi(section.band2.length)})
              </summary>
              {section.band2.map((box) => (
                <Box key={box.key} box={box} yenile={() => void load()} />
              ))}
            </details>
          ) : null}

          <h3>{metin.bant3}</h3>
          <ul>
            {section.band3.map((entry) => (
              <li key={entry.key}>
                <strong>{entry.label}:</strong> {entry.detail}
              </li>
            ))}
          </ul>
          <p className="source">
            {metin.okunmaAni} {tarihSaat(section.generatedAt)}
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * Girişte düşülecek adres (görev CRM-A14 kapanışı: "gezinme rolün gördüğü bölümlere bağlı, ama
 * giriş hâlâ genel bakışa düşüyor").
 *
 * Yalnız **ilk** kontrolde çalışır: `sections` ilk dolduğunda, o an genel bakıştaysak rolün tek
 * bölümüne gönderir (`yonlendirme.ts` `girisYolu`). Sonrasında bir daha araya girmez — kullanıcı
 * menüden "Genel Bakış"a dönerse orada kalır, yoksa o bağlantı tek rollü bir kullanıcı için hiç
 * işe yaramazdı.
 *
 * Bu bileşen `<Workspace>`in (App.tsx) her sayfasında bir kere kurulu durur ve hiçbir şey
 * çizmez; işi yalnızca adresi değiştirmek.
 */
export function GirisYonlendirmesi({ sections }: { sections: DashboardSectionIndex | null }) {
  const { yol, git } = useYol();
  const ilkKontrol = useRef(true);

  useEffect(() => {
    if (!ilkKontrol.current || !sections) return;
    ilkKontrol.current = false;
    if (yol !== YOLLAR.genelBakis) return;
    const hedef = girisYolu(sections.sections);
    if (hedef !== YOLLAR.genelBakis) git(hedef);
  }, [sections, yol, git]);

  return null;
}

/**
 * Rolün gördüğü bölümler, ürün sahibinin kuralı: *"diğer hiçbir kullanıcı atandığından
 * başkasını görmüyor."* Liste uçtan geliyor; tarayıcı hiçbir bölümü kendi kendine eklemiyor,
 * ve eklese de uçlar reddediyor.
 */
export function DashboardPanels({ sections, only }: { sections: DashboardSectionIndex | null; only?: string }) {
  if (!sections) return null;
  /**
   * `only` verildiğinde yalnız o bölüm çiziliyor.
   *
   * Sayfalara bölünmenin asıl kazancı burada: eskiden dört bölümün dördü birden çiziliyor ve
   * dördünün verisi birden iniyordu. Artık açılan sayfa hangisiyse yalnız onun ucu çağrılıyor.
   */
  const ordered = SECTION_ORDER.filter(
    (key) => (only === undefined || key === only) && sections.sections.some((section) => section.key === key),
  );
  if (ordered.length === 0) {
    return (
      <section data-testid="dashboard-none">
        <h2>{PANO_METIN.bos.baslik}</h2>
        <p className="muted">{PANO_METIN.bos.tarif}</p>
      </section>
    );
  }
  return (
    <>
      {ordered.map((key) => (
        <SectionScreen key={key} sectionKey={key} />
      ))}
    </>
  );
}
